import React, { useCallback, useState } from 'react';
import { StyleSheet } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Divider, Stack } from '@/components/layout';
import { Button, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { AuthTextField } from '@/features/auth/components/auth-text-field';
import { GuardianProfileActionHeader } from '@/features/guardian-profile/components';
import { useGuardianChangePassword } from '@/features/guardian-profile/hooks/use-guardian-change-password';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';

const strings = STRINGS.guardian.changePassword;
const authChangePasswordStrings = STRINGS.auth.changePassword;

export function GuardianChangePasswordScreen() {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();

  const {
    currentPassword,
    newPassword,
    confirmPassword,
    confirmMismatchError,
    fieldErrors,
    onChangeCurrentPassword,
    onChangeNewPassword,
    onChangeConfirmPassword,
    handleCancel,
    handleUpdatePassword,
  } = useGuardianChangePassword();

  const [currentSecureTextEntry, setCurrentSecureTextEntry] = useState(true);
  const [newSecureTextEntry, setNewSecureTextEntry] = useState(true);
  const [confirmSecureTextEntry, setConfirmSecureTextEntry] = useState(true);

  const handleSave = useCallback(() => {
    handleUpdatePassword();
  }, [handleUpdatePassword]);

  const fields = (
    <Stack gap={scale({ phone: 14, tablet: 16 })}>
      <AuthTextField
        label={strings.currentPasswordLabel}
        required
        placeholder={authChangePasswordStrings.placeholderCurrent}
        value={currentPassword}
        onChangeText={onChangeCurrentPassword}
        secureTextEntry={currentSecureTextEntry}
        onSecureTextEntryChange={setCurrentSecureTextEntry}
        error={fieldErrors.currentPassword}
        fieldProps={{
          maxLength: 50,
          autoCapitalize: 'none',
          autoCorrect: false,
          textContentType: 'password',
          autoComplete: 'current-password',
        }}
      />

      <AuthTextField
        label={strings.newPasswordLabel}
        required
        placeholder={authChangePasswordStrings.placeholderNew}
        value={newPassword}
        onChangeText={onChangeNewPassword}
        secureTextEntry={newSecureTextEntry}
        onSecureTextEntryChange={setNewSecureTextEntry}
        error={fieldErrors.newPassword}
        fieldProps={{
          maxLength: 50,
          autoCapitalize: 'none',
          autoCorrect: false,
          textContentType: 'newPassword',
          autoComplete: 'new-password',
        }}
      />

      <AuthTextField
        label={isTablet ? strings.confirmNewPasswordLabel : strings.confirmPasswordLabel}
        required
        placeholder={authChangePasswordStrings.placeholderConfirm}
        value={confirmPassword}
        onChangeText={onChangeConfirmPassword}
        secureTextEntry={confirmSecureTextEntry}
        onSecureTextEntryChange={setConfirmSecureTextEntry}
        error={confirmMismatchError ?? fieldErrors.confirmPassword}
        fieldProps={{
          maxLength: 50,
          autoCapitalize: 'none',
          autoCorrect: false,
          textContentType: 'newPassword',
          autoComplete: 'new-password',
        }}
      />
    </Stack>
  );

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <GuardianProfileActionHeader
        title={strings.title}
        isTablet={isTablet}
        topInset={insets.top}
        onBack={handleCancel}
        rightAction={{ label: strings.save, onPress: handleSave }}
      />
      {isTablet ? (
        <Divider
          color={guardianColors['3']}
          spacing={scale(0)}
          thickness={StyleSheet.hairlineWidth}
        />
      ) : null}

      <KeyboardAwareScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          isTablet ? styles.contentTablet : styles.contentPhone,
          { paddingBottom: insets.bottom + scale({ phone: 24, tablet: 24 }) },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bottomOffset={scale(12)}
        extraKeyboardSpace={scale(12)}>
        <Stack style={isTablet ? styles.formTablet : styles.formPhone}>
          <Stack gap={scale({ phone: 12, tablet: 20 })}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              style={styles.minimumHint}>
              {strings.minimumHint}
            </Text>
            {fields}
          </Stack>

          {isTablet ? (
            <Button onPress={handleSave} style={styles.tabletSaveButton}>
              <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 SemiBold">
                {strings.save}
              </Button.Text>
            </Button>
          ) : null}
        </Stack>
      </KeyboardAwareScrollView>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  scroll: {
    flex: 1,
    backgroundColor: guardianColors['1'],
  },
  content: {
    flexGrow: 1,
  },
  contentPhone: {
    paddingTop: scale(12),
    paddingHorizontal: scale(16),
  },
  contentTablet: {
    paddingTop: scale(16),
    paddingHorizontal: scale(24),
    alignItems: 'center',
  },
  formPhone: {
    width: '100%',
  },
  formTablet: {
    width: '100%',
    maxWidth: scale(380),
  },
  minimumHint: {
    color: guardianColors['6'],
  },
  tabletSaveButton: {
    alignSelf: 'stretch',
    marginTop: scale(20),
    height: scale(48),
    minHeight: scale(48),
    borderRadius: scale(10),
    borderCurve: 'continuous',
    paddingVertical: scale(0),
    backgroundColor: guardianColors['Main/Main'],
  },
}));
